export function parseToolContent(content) {
  try {
    return JSON.parse(content)
  } catch {
    return null
  }
}

export function getLatestMessage(messages, role) {
  const filtered = (messages || []).filter((msg) => msg.role === role)
  return filtered[filtered.length - 1]
}

function normalizeDraft(draft) {
  const data = draft?.draft_data || draft || {}
  return {
    id: draft?.id,
    title: draft?.title || data.title || data.summary,
    status: draft?.status || 'draft',
    destination_provider: draft?.destination_provider,
    external_key: draft?.external_key || draft?.jira_issue_key,
    jira_issue_key: draft?.jira_issue_key,
    acceptance_criteria: draft?.acceptance_criteria || data.acceptance_criteria || data.acceptanceCriteria || [],
    parent_draft_id: draft?.parent_draft_id ?? data.parent_draft_id,
    hierarchy_role: draft?.hierarchy_role || data.hierarchy_role || 'standalone',
    issue_type: draft?.issue_type || data.issue_type || data.issueType,
    story_points: draft?.story_points ?? data.story_points ?? data.storyPoints,
    domain: draft?.domain || data.domain,
    split_reason: draft?.split_reason || data.split_reason || data.splitReason,
    draft_data: data,
    evidence: draft?.evidence || {},
    preview: draft?.preview || null,
    destination_url: draft?.destination_url || null,
  }
}

function draftsFromMessages(session) {
  const messages = session?.messages || []
  const fromUi = messages
    .flatMap((m) => (m?.ui_payload?.ui_action?.type === 'draft_review' ? m.ui_payload.ui_action.drafts || [] : []))

  const fromTools = messages
    .filter((m) => m.role === 'tool')
    .flatMap((m) => {
      const parsed = parseToolContent(m.content)
      return parsed?.result?.drafts || parsed?.drafts || []
    })

  const merged = new Map()
  ;[...fromUi, ...fromTools].forEach((draft) => {
    if (!draft?.id) return
    merged.set(draft.id, normalizeDraft(draft))
  })
  return Array.from(merged.values())
}

export function extractDrafts(session) {
  const fromDb = (session?.ticket_drafts || []).map(normalizeDraft)
  const fromMessages = draftsFromMessages(session)
  const merged = new Map()
  ;[...fromDb, ...fromMessages].forEach((draft) => {
    if (!draft?.id) return
    merged.set(draft.id, normalizeDraft(draft))
  })
  return Array.from(merged.values())
}

const CREATION_TOOLS = new Set(['asana_create_task', 'jira_create_issue'])

function creationPayloadFromToolMessage(parsed) {
  if (!parsed) return null
  const error = parsed?.error || parsed?.result?.error
  if (error) return null

  const payload = parsed?.result?.result || parsed?.result
  if (payload?.gid) return { provider: 'asana', externalKey: String(payload.gid) }
  if (payload?.key) return { provider: 'jira', externalKey: payload.key }
  return null
}

function getDraftCreationResultForId(messages, draftId) {
  const list = messages || []
  for (let i = 0; i < list.length; i += 1) {
    const message = list[i]
    if (message?.role !== 'assistant') continue

    for (const call of message.tool_calls || []) {
      const name = call?.function?.name
      if (!CREATION_TOOLS.has(name)) continue

      let args = {}
      try {
        args = JSON.parse(call.function?.arguments || '{}')
      } catch {
        args = {}
      }
      if (Number(args.draft_id) !== Number(draftId)) continue

      const resultMsg = list.slice(i + 1).find((m) => {
        if (m.role !== 'tool') return false
        const tr = m.tool_results || {}
        return tr.tool_call_id === call.id || tr['tool_call_id'] === call.id
      })
      if (!resultMsg) continue

      const payload = creationPayloadFromToolMessage(parseToolContent(resultMsg.content))
      if (payload) return payload
    }
  }
  return null
}

function findLatestCreationResult(messages) {
  const list = messages || []
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const message = list[i]
    if (message?.role !== 'tool') continue
    const parsed = parseToolContent(message.content)
    if (!CREATION_TOOLS.has(parsed?.tool_name)) continue
    const payload = creationPayloadFromToolMessage(parsed)
    if (payload) return payload
  }
  return null
}

function parseAsanaGidFromAssistant(messages) {
  const list = messages || []
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const message = list[i]
    if (message?.role !== 'assistant' || !message.content) continue
    const match = message.content.match(/task\s+(?:ID|id)\s+is\s+(\d+)/i)
    if (match) return match[1]
  }
  return null
}

export function getDraftPublication(draft, session) {
  const messages = session?.messages || []
  const draftCount = extractDrafts(session).filter((d) => d.status !== 'rejected').length

  if (draft.status === 'created') {
    const provider = draft.destination_provider || (draft.jira_issue_key ? 'jira' : 'asana')
    return {
      published: true,
      provider,
      externalKey: draft.external_key || draft.jira_issue_key,
    }
  }

  const specific = getDraftCreationResultForId(messages, draft.id)
  if (specific) return { published: true, ...specific }

  if (draftCount === 1) {
    const latest = findLatestCreationResult(messages)
    if (latest) return { published: true, ...latest }

    const chatGid = parseAsanaGidFromAssistant(messages)
    if (chatGid) return { published: true, provider: 'asana', externalKey: chatGid }
  }

  return { published: false, provider: null, externalKey: null }
}

function draftCreationSucceeded(messages, draftId) {
  return getDraftCreationResultForId(messages, draftId) != null
}

export function isReviewableDraft(draft, messages = [], session = null) {
  if (!draft?.id) return false
  if (draft.published) return false
  if (draft.status === 'created' || draft.status === 'rejected') return false
  if (session && getDraftPublication(draft, session).published) return false
  if (draftCreationSucceeded(messages, draft.id)) return false
  return true
}

export function enrichDraftForPanel(draft, session) {
  const publication = getDraftPublication(draft, session)
  const provider = publication.provider || draft.destination_provider
  const externalKey = publication.externalKey || draft.external_key || draft.jira_issue_key

  return {
    ...draft,
    published: publication.published,
    destination_provider: provider,
    external_key: externalKey,
    jira_issue_key: draft.jira_issue_key || (provider === 'jira' ? externalKey : null),
  }
}

export function extractReviewableDrafts(session) {
  return extractDrafts(session)
    .map((draft) => enrichDraftForPanel(draft, session))
    .filter((draft) => draft.status !== 'rejected' && isReviewableDraft(draft, session?.messages, session))
}

export function extractPanelDrafts(session) {
  return extractDrafts(session)
    .filter((draft) => draft.status !== 'rejected')
    .map((draft) => enrichDraftForPanel(draft, session))
    .filter((draft) => draft.published || isReviewableDraft(draft, session?.messages, session))
}

function latestRoleIndex(messages, role) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === role) return i
  }
  return -1
}

export function extractLatestUiAction(messages) {
  const list = messages || []
  const lastAssistantIdx = latestRoleIndex(list, 'assistant')
  const lastUserIdx = latestRoleIndex(list, 'user')

  // User replied after the latest assistant turn — prior CTAs are resolved.
  if (lastUserIdx > lastAssistantIdx) return null

  for (let i = list.length - 1; i >= 0; i -= 1) {
    const message = list[i]
    const fromPayload = message?.ui_payload?.ui_action
    if (fromPayload) return fromPayload

    if (message?.role === 'tool') {
      const parsed = parseToolContent(message.content)
      const fromTool = parsed?.result?.ui_action || parsed?.ui_action
      if (fromTool) return fromTool
    }

    // Once ARIA produces a later assistant turn with visible text but no
    // ui_action, any earlier action (e.g. an oauth_connect card) is stale.
    if (message?.role === 'assistant' && message?.content) return null
  }
  return null
}
